/**
 * One section in the workbench accordion. The header shows the heading, a
 * content-type Pill and a one-line summary; clicking it selects the section
 * (highlighting it in the preview) and toggles the body. In edit mode the header
 * adds an inline-rename, a drag handle and a kebab menu, and the body adds a
 * content-type switcher above the per-type config editor.
 */

import { ChevronDownIcon, DotsVerticalIcon, DragHandleDots2Icon } from '@radix-ui/react-icons';
import { Collapsible, DropdownMenu, InlineEdit, Pill, PillRadio } from '@protoss/ui';
import type { DocHeading, PreviewSample } from '../../../../components/doc';
import {
  CONTENT_TYPE_META,
  SELECTABLE_CONTENT_TYPES,
  sectionLabel,
  sectionSummary,
  type LogicalContentType,
  type LogicalSection,
} from '../../lib/sowContract';
import { SowSectionConfigEditor } from './SowSectionConfigEditor';

export interface SowSectionRowProps {
  section: LogicalSection;
  isEditing: boolean;
  selected: boolean;
  expanded: boolean;
  sample: PreviewSample;
  headings: DocHeading[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  isDragOver: boolean;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, text: string) => void;
  onSwitchType: (id: string, type: LogicalContentType) => void;
  onChange: (section: LogicalSection) => void;
  onSampleChange: (sample: PreviewSample) => void;
  onMove: (id: string, dir: 'up' | 'down') => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragOverRow: (id: string) => void;
  onDrop: (id: string) => void;
  onDragEnd: () => void;
}

export function SowSectionRow(props: SowSectionRowProps) {
  const {
    section,
    isEditing,
    selected,
    expanded,
    sample,
    headings,
    canMoveUp,
    canMoveDown,
    isDragOver,
    onSelect,
    onToggle,
    onRename,
    onSwitchType,
    onChange,
    onSampleChange,
    onMove,
    onDuplicate,
    onRemove,
    onDragStart,
    onDragOverRow,
    onDrop,
    onDragEnd,
  } = props;

  const meta = CONTENT_TYPE_META[section.contentType];
  const canRename = isEditing && !section.isPreamble;
  const canSwitch = isEditing && !section.isPreamble && section.contentType !== 'break';

  const activate = () => {
    onSelect(section.id);
    onToggle(section.id);
  };

  return (
    <div
      className={[
        'ptp-sow-section',
        selected ? 'ptp-sow-section--selected' : '',
        expanded ? 'ptp-sow-section--open' : '',
        isDragOver ? 'ptp-sow-section--drag-over' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      draggable={isEditing}
      onDragStart={() => onDragStart(section.id)}
      onDragOver={(e) => {
        if (!isEditing) return;
        e.preventDefault();
        onDragOverRow(section.id);
      }}
      onDrop={() => onDrop(section.id)}
      onDragEnd={onDragEnd}
    >
      <Collapsible.Root open={expanded}>
        <div
          className="ptp-sow-section__head"
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={activate}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              activate();
            }
          }}
        >
          {isEditing ? (
            <span
              className="ptp-sow-section__drag"
              aria-hidden
              onClick={(e) => e.stopPropagation()}
            >
              <DragHandleDots2Icon />
            </span>
          ) : null}

          <span className="ptp-sow-section__title" onClick={(e) => canRename && e.stopPropagation()}>
            {canRename ? (
              <InlineEdit
                value={sectionLabel(section)}
                onSave={(v) => onRename(section.id, v)}
                editLabel="Rename section"
              />
            ) : (
              sectionLabel(section)
            )}
          </span>

          <Pill variant={meta.tone}>{meta.label}</Pill>
          <span className="ptp-sow-section__summary">{sectionSummary(section)}</span>

          {isEditing ? (
            <span onClick={(e) => e.stopPropagation()}>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button type="button" className="ptp-sow-section__menu" aria-label="Section actions">
                    <DotsVerticalIcon />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="dropdown-content">
                  <DropdownMenu.Item
                    className="dropdown-item"
                    disabled={!canMoveUp}
                    onSelect={() => onMove(section.id, 'up')}
                  >
                    Move up
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="dropdown-item"
                    disabled={!canMoveDown}
                    onSelect={() => onMove(section.id, 'down')}
                  >
                    Move down
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="dropdown-item"
                    onSelect={() => onDuplicate(section.id)}
                  >
                    Duplicate
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="dropdown-separator" />
                  <DropdownMenu.Item
                    className="dropdown-item dropdown-item--danger"
                    disabled={section.isPreamble}
                    onSelect={() => onRemove(section.id)}
                  >
                    Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </span>
          ) : null}

          <ChevronDownIcon className="ptp-sow-section__chevron" data-expanded={expanded || undefined} />
        </div>

        <Collapsible.Content className="ptp-sow-section__body">
          {canSwitch ? (
            <div className="ptp-sow-section__type-switch">
              <span className="ptp-sow-section__type-label">Content type</span>
              <PillRadio
                options={SELECTABLE_CONTENT_TYPES.map((t) => ({
                  value: t,
                  label: CONTENT_TYPE_META[t].label,
                }))}
                selectedValue={
                  SELECTABLE_CONTENT_TYPES.includes(section.contentType) ? section.contentType : null
                }
                onSelectionChange={(v) => v && onSwitchType(section.id, v as LogicalContentType)}
              />
            </div>
          ) : null}

          <SowSectionConfigEditor
            section={section}
            isEditing={isEditing}
            sample={sample}
            headings={headings}
            onChange={onChange}
            onSampleChange={onSampleChange}
          />
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}
